import { useEffect, useState } from 'react'
import { readDismissed, shouldShowBanner, writeDismissed } from './banner'
import { pushState, sendTest, subscribe, type PushState } from './push'

/*
 * The front door to notifications.
 *
 * The Reminders card on Profile has always worked. Eleven of twelve players
 * had never found it, which is a discoverability problem and not a push
 * problem - the control existed, three taps deep, on the screen nobody opens.
 *
 * THE TAP IS THE PERMISSION GESTURE, deliberately. Nothing here prompts on
 * load. A permission prompt nobody asked for is the fastest route to a
 * permanent `denied`, and `denied` is the one state no banner can ever undo:
 * the browser will not ask again, and the only cure is buried in site
 * settings. So the app asks for a tap first and spends the one prompt it gets
 * on somebody who has already said yes to something.
 *
 * IT SENDS A TEST THE MOMENT IT SUCCEEDS. Push in this app has never been
 * confirmed arriving on a single phone - see docs/STATE.md - and a channel
 * that fails silently is worse than no channel, because the board goes up and
 * everybody believes they were told. Subscribing and proving the chain are one
 * motion here, so the first notification a person ever gets is the one that
 * demonstrates they will get them, and a refusal from the push service arrives
 * as words on the screen instead of as nothing at all.
 */
export function PushBanner({
  playerId,
  onOpenProfile,
}: {
  /** Who this device would subscribe. Null before anybody has been chosen. */
  playerId: string | null
  onOpenProfile: () => void
}) {
  const [ps, setPs] = useState<PushState | null>(null)
  const [show, setShow] = useState(false)
  const [steps, setSteps] = useState(false)
  const [busy, setBusy] = useState(false)
  /* Set once the subscribe attempt has something to report, and it outranks
     everything below: `ps` flips to 'on' on success, which would otherwise
     unmount the banner before it could say so. */
  const [said, setSaid] = useState<string | null>(null)

  useEffect(() => {
    setShow(shouldShowBanner(readDismissed(), Date.now()))
    void pushState().then(setPs)
  }, [])

  const close = () => {
    writeDismissed(Date.now())
    setShow(false)
  }

  const turnOn = async () => {
    // Nobody is selected, so there is no one to subscribe. Profile is where
    // that gets fixed, and it is also where the full control lives.
    if (playerId === null) {
      onOpenProfile()
      return
    }
    setBusy(true)
    try {
      const next = await subscribe([playerId])
      setPs(next)
      if (next !== 'on') return
      const t = await sendTest()
      setSaid(
        t.ok
          ? 'Reminders on. A test just went out — did it arrive?'
          : `Subscribed, but the push service refused a test. ${t.detail}`,
      )
    } catch (e) {
      setSaid(e instanceof Error ? e.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  if (!show) return null
  if (said === null && (ps === null || ps === 'unsupported' || ps === 'on')) return null

  return (
    <div className="slate-num mx-3 mb-2.5 border border-hairline bg-card">
      <div className="flex items-stretch">
        {said !== null ? (
          <button
            type="button"
            onClick={() => {
              close()
              onOpenProfile()
            }}
            className="slate-tap flex-1 px-3 py-2 text-left text-[10px] leading-[1.5] font-bold tracking-[0.08em] text-slate uppercase"
          >
            {said}
            {/* Device-scoped, and this phone carries several people. The
                eight-year-old picks on his father's. */}
            <span className="block pt-0.5 text-faint">
              Also for others on this phone &rarr;
            </span>
          </button>
        ) : ps === 'blocked' ? (
          <span className="flex-1 px-3 py-2 text-[10px] leading-[1.5] font-bold tracking-[0.08em] text-slate uppercase">
            Notifications are blocked for this site in your browser settings
          </span>
        ) : ps === 'needs-install' ? (
          <button
            type="button"
            onClick={() => setSteps(!steps)}
            aria-expanded={steps}
            className="slate-tap flex-1 px-3 py-2 text-left text-[10px] font-bold tracking-[0.12em] text-slate uppercase"
          >
            &#9679; Get reminders &mdash; how? {steps ? '∧' : '∨'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void turnOn()}
            disabled={busy}
            className="slate-tap flex-1 px-3 py-2 text-left text-[10px] font-bold tracking-[0.12em] text-slate uppercase"
          >
            &#9679; {busy ? 'Turning on…' : 'Turn on reminders'}
          </button>
        )}

        {/*
         * Closing is a decision, so it gets a real target rather than a 12px
         * glyph. It comes back in a week - see shouldShowBanner - because a
         * prompt dismissed forever recruits only whoever was paying attention
         * that day.
         */}
        <button
          type="button"
          onClick={close}
          aria-label="Hide this for now"
          className="slate-tap min-w-11 px-3 text-[13px] text-faint"
        >
          &times;
        </button>
      </div>

      {/*
       * iOS has no install prompt to trigger - `beforeinstallprompt` does not
       * exist in Safari - so the only thing the app can do is say where the
       * button is. Three lines, in place, rather than a trip to Profile.
       */}
      {steps && ps === 'needs-install' && (
        <ol className="list-none border-t border-hairline px-3 py-2 text-[10px] leading-[1.7] tracking-[0.06em] text-slate uppercase">
          <li>1. Tap the share button in Safari&rsquo;s bar</li>
          <li>2. Choose &ldquo;Add to Home Screen&rdquo;</li>
          <li>3. Open Pick Six from your home screen</li>
        </ol>
      )}
    </div>
  )
}
